import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Star, ThumbsUp } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const Reviews = () => {
  const { t } = useTranslation();
  const [reviews, setReviews] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    rating: 5,
    comment: ''
  });

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/reviews`);
      const data = await response.json();
      
      if (data.success) {
        setReviews(data.reviews);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRatingClick = (rating) => {
    setFormData(prev => ({
      ...prev,
      rating
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/reviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success(t('reviews.form.success'));
        setFormData({
          name: '',
          rating: 5,
          comment: ''
        });
        setIsDialogOpen(false);
        // Refresh reviews
        fetchReviews();
      } else {
        toast.error(data.detail || t('reviews.form.error'));
      }
    } catch (error) {
      console.error('Review submission error:', error);
      toast.error(t('reviews.form.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStars = (rating, interactive = false, size = 'w-5 h-5') => {
    return Array.from({ length: 5 }).map((_, index) => (
      <Star
        key={index}
        className={`${size} cursor-pointer transition-all ${
          index < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
        } ${interactive ? 'hover:fill-yellow-300 hover:text-yellow-300' : ''}`}
        onClick={() => interactive && handleRatingClick(index + 1)}
      />
    ));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('az-AZ', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const averageRating = reviews.length > 0
    ? (reviews.reduce((acc, review) => acc + review.rating, 0) / reviews.length).toFixed(1)
    : '0.0';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-red-600 to-red-700 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-5xl font-bold mb-6">{t('reviews.title')}</h1>
            <p className="text-xl text-red-100 leading-relaxed">
              {t('reviews.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Rating Summary */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8">
                <div className="grid md:grid-cols-3 gap-8 items-center">
                  <div className="text-center md:border-r">
                    <div className="text-6xl font-bold text-red-600 mb-2">{averageRating}</div>
                    <div className="flex justify-center mb-2">{renderStars(5)}</div>
                    <p className="text-gray-600 text-sm">{reviews.length} {t('reviews.stats.total')}</p>
                  </div>
                  <div className="md:col-span-2">
                    <h3 className="text-2xl font-bold mb-4">{t('reviews.stats.happy')}</h3>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      {t('reviews.stats.description')}
                    </p>
                    
                    {/* Review Dialog */}
                    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                      <DialogTrigger asChild>
                        <Button className="bg-red-600 hover:bg-red-700 text-white">
                          {t('reviews.writeReview')}
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-[500px]">
                        <DialogHeader>
                          <DialogTitle className="text-2xl">{t('reviews.form.title')}</DialogTitle>
                          <DialogDescription>
                            {t('reviews.subtitle')}
                          </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                          <div className="space-y-2">
                            <Label htmlFor="name">{t('reviews.form.name')} *</Label>
                            <Input
                              id="name"
                              name="name"
                              value={formData.name}
                              onChange={handleChange}
                              placeholder={t('reviews.form.namePlaceholder')}
                              required
                              className="border-gray-300 focus:border-red-500 focus:ring-red-500"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label>{t('reviews.form.rating')} *</Label>
                            <div className="flex gap-2">
                              {renderStars(formData.rating, true, 'w-8 h-8')}
                            </div>
                            <p className="text-sm text-gray-600">
                              {formData.rating} {t('reviews.stats.total')}
                            </p>
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="comment">{t('reviews.form.comment')} *</Label>
                            <Textarea
                              id="comment"
                              name="comment"
                              value={formData.comment}
                              onChange={handleChange}
                              placeholder={t('reviews.form.commentPlaceholder')}
                              rows={5}
                              required
                              minLength={10}
                              className="border-gray-300 focus:border-red-500 focus:ring-red-500 resize-none"
                            />
                          </div>

                          <Button
                            type="submit"
                            disabled={isSubmitting}
                            className="w-full bg-red-600 hover:bg-red-700 text-white"
                          >
                            {isSubmitting ? t('reviews.form.submitting') : t('reviews.form.submit')}
                          </Button>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Reviews Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
                <p className="text-gray-600">{t('reviews.loading')}</p>
              </div>
            ) : reviews.length === 0 ? (
              <Card className="border-0 shadow-lg">
                <CardContent className="p-12 text-center">
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">
                    Hələ rəy yoxdur
                  </h3>
                  <p className="text-gray-600 mb-4">
                    İlk rəyi siz yazın!
                  </p>
                  <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-red-600 hover:bg-red-700 text-white">
                        Rəy yazın
                      </Button>
                    </DialogTrigger>
                  </Dialog>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {reviews.map((review) => (
                  <Card key={review.id} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                    <CardContent className="p-6">
                      {/* Rating */}
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex">{renderStars(review.rating)}</div>
                        <span className="text-sm text-gray-500">{formatDate(review.date)}</span>
                      </div>

                      {/* Comment */}
                      <p className="text-gray-700 leading-relaxed mb-4 text-base">
                        "{review.comment}"
                      </p>

                      {/* Author */}
                      <div className="flex items-center justify-between pt-4 border-t">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                            <span className="text-red-600 font-semibold text-lg">
                              {review.name.charAt(0)}
                            </span>
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{review.name}</p>
                            <p className="text-xs text-gray-500">Təsdiqlənmiş müştəri</p>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" className="text-gray-500 hover:text-red-600">
                          <ThumbsUp className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">{t('reviews.shareExperience')}</h2>
            <p className="text-gray-600 mb-8 leading-relaxed">
              {t('reviews.shareDesc')}
            </p>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8">
                  {t('reviews.writeReview')}
                </Button>
              </DialogTrigger>
            </Dialog>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Reviews;
